import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, AlertTriangle, CheckCircle2, XCircle, Truck, Clock, FileText, MapPin, Copy, ExternalLink, Store, Plus } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { formatGs, ORDER_STATUS } from '@/utils/helpers';
import { toast } from 'sonner';

const TRANSITIONS = {
  pendiente: [{ to: 'cocina', label: 'Aceptar / Enviar a Cocina', style: 'bg-orange-500 hover:bg-orange-600' }, { to: 'rechazado', label: 'Rechazar', style: 'bg-red-600 hover:bg-red-700', needsReason: true }],
  cocina: [{ to: 'en_camino', label: 'En Camino', style: 'bg-blue-500 hover:bg-blue-600' }, { to: 'cancelado', label: 'Cancelar', style: 'bg-red-600 hover:bg-red-700' }],
  en_camino: [{ to: 'entregado', label: 'Entregado', style: 'bg-emerald-500 hover:bg-emerald-600' }, { to: 'cancelado', label: 'Cancelar', style: 'bg-red-600 hover:bg-red-700' }],
};

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingPickup, setUpdatingPickup] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [manualCopyUrl, setManualCopyUrl] = useState(null);
  const ticketRef = useRef(null);
  const manualInputRef = useRef(null);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Enlace copiado', { duration: 2000 });
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.cssText = 'position:fixed;opacity:0;left:-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Enlace copiado', { duration: 2000 });
      } catch {
        setManualCopyUrl(text);
        toast.warning('No se pudo copiar automáticamente. Copiá manualmente.', { duration: 3000 });
      }
    }
  };

  const fetchOrder = () => {
    api.get(`/pedidos/${id}`).then(r => setOrder(r.data.pedido)).catch(() => toast.error('Error al cargar pedido')).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/pedidos/${id}/estado`, { status: newStatus });
      setOrder(prev => ({ ...prev, ...data.pedido, items: prev.items, status_history: prev.status_history }));
      toast.success(data.mensaje);
      // Refresh full order
      const { data: fresh } = await api.get(`/pedidos/${id}`);
      setOrder(fresh.pedido);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar estado');
    }
    setUpdating(false);
    setShowReject(false);
  };

  const handlePrint = () => {
    const content = ticketRef.current;
    if (!content) return;
    const win = window.open('', '', 'width=300,height=600');
    win.document.write(`<html><head><title>Pedido #${order.id}</title><style>body{font-family:monospace;font-size:12px;margin:8px;color:#000}h2{text-align:center;margin:4px 0}hr{border:none;border-top:1px dashed #000}.row{display:flex;justify-content:space-between}.bold{font-weight:bold}.note{background:#fff3cd;padding:4px;margin:4px 0;border:1px solid #ffc107}</style></head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    win.print();
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[#E84C1C] border-t-transparent rounded-full animate-spin" /></div>;
  if (!order) return <div className="text-center text-zinc-500 py-12">Pedido no encontrado.</div>;

  const st = ORDER_STATUS[order.status] || { label: order.status, color: 'bg-zinc-500/20 text-zinc-400' };
  const transitions = TRANSITIONS[order.status] || [];

  return (
    <div data-testid="admin-order-detail">
      <button onClick={() => navigate('/admin/pedidos')} className="flex items-center gap-1 text-sm text-zinc-400 hover:text-white mb-4 transition-colors">
        <ArrowLeft size={16} /> Volver a pedidos
      </button>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Left - Order info */}
        <div className="flex-1 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Pedido <span className="text-[#E84C1C]">#{order.id}</span></h2>
              <p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleString('es-PY')}</p>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${st.color}`} data-testid="order-status-chip">{st.label}</span>
          </div>

          {/* Customer */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 space-y-2">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Cliente</h3>
            <p className="text-sm font-medium">{order.customer_name}</p>
            <p className="text-xs text-zinc-400">{order.customer_email} {order.customer_phone && `- ${order.customer_phone}`}</p>
            {order.address_line && <p className="text-xs text-zinc-400 flex items-center gap-1"><Truck size={12} /> {order.address_line}</p>}
            {order.delivery_address_text && <p className="text-xs text-zinc-300 flex items-center gap-1 mt-0.5"><MapPin size={12} className="text-[#E84C1C]" /> {order.delivery_address_text}</p>}
          </div>

          {/* Pickup info */}
          {order.order_type === 'pickup' && (
            <div data-testid="admin-pickup-block" className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
              <h3 className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-2 flex items-center gap-2"><Store size={14} /> Retiro en local</h3>
              {order.pickup_ready_at && (
                <p className="text-sm font-medium text-amber-300 mb-2">Hora estimada: {new Date(order.pickup_ready_at).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</p>
              )}
              <div className="flex flex-wrap gap-2">
                {[5, 10, 15].map(min => (
                  <button
                    key={min}
                    data-testid={`pickup-add-${min}`}
                    disabled={updatingPickup}
                    onClick={async () => {
                      setUpdatingPickup(true);
                      try {
                        const { data } = await api.patch(`/pedidos/${order.id}/pickup-time`, { add_minutes: min });
                        toast.success(data.mensaje);
                        fetchOrder();
                      } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
                      setUpdatingPickup(false);
                    }}
                    className="flex items-center gap-1 text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1.5 rounded-lg hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                  >
                    <Plus size={12} /> {min} min
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Location / Delivery Address */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4" data-testid="order-location-block">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Dirección de entrega</h3>
            {order.delivery_address_text && (
              <p data-testid="order-delivery-address" className="text-sm font-medium mb-2">{order.delivery_address_text}</p>
            )}
            {order.address_label && (
              <p className="text-xs text-zinc-400 mb-1">{order.address_label}: {order.saved_address_text || order.address_line}</p>
            )}
            {order.lat && order.lng ? (() => {
              const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${order.lat},${order.lng}`;
              const openMaps = () => window.open(mapsUrl, '_blank', 'noopener,noreferrer');
              return (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-400 font-mono">{parseFloat(order.lat).toFixed(6)}, {parseFloat(order.lng).toFixed(6)}</p>
                  <div className="flex gap-2">
                    <button onClick={openMaps} data-testid="order-maps-btn" className="flex items-center gap-1.5 text-xs bg-[#E84C1C]/10 text-[#E84C1C] border border-[#E84C1C]/20 px-3 py-2 rounded-lg hover:bg-[#E84C1C]/20 transition-colors">
                      <ExternalLink size={13} /> Abrir en Google Maps
                    </button>
                    <button onClick={() => copyToClipboard(mapsUrl)} data-testid="order-copy-maps-btn" className="flex items-center gap-1.5 text-xs text-zinc-400 border border-zinc-700 px-3 py-2 rounded-lg hover:bg-zinc-800 transition-colors">
                      <Copy size={13} /> Copiar enlace
                    </button>
                  </div>
                </div>
              );
            })() : (
              <p className="text-xs text-zinc-500 flex items-center gap-1.5"><MapPin size={13} className="text-zinc-600" /> El cliente no confirmó ubicación.</p>
            )}
          </div>
          {order.nota_general && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex gap-3" data-testid="order-note">
              <FileText size={18} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-amber-300 uppercase tracking-wider">Nota del cliente</p>
                <p className="text-sm text-zinc-200 mt-1">{order.nota_general}</p>
              </div>
            </div>
          )}

          {/* Billing info */}
          {(order.factura_denominacion || order.factura_ruc) && (
            <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4" data-testid="order-billing">
              <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Datos de facturación</h3>
              <div className="space-y-1 text-sm">
                {order.factura_denominacion && <p className="text-zinc-200"><span className="text-zinc-500 text-xs">Denominación:</span> {order.factura_denominacion}</p>}
                {order.factura_ruc && <p className="text-zinc-200"><span className="text-zinc-500 text-xs">RUC:</span> {order.factura_ruc}</p>}
                {order.factura_email && <p className="text-zinc-200"><span className="text-zinc-500 text-xs">Email factura:</span> {order.factura_email}</p>}
              </div>
            </div>
          )}

          {/* Items */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider p-4 pb-2">Items del pedido</h3>
            <div className="divide-y divide-zinc-800/30">
              {(order.items || []).map(item => (
                <div key={item.id} className="px-4 py-3 flex justify-between">
                  <div>
                    <p className="text-sm">{item.quantity}x {item.product_name}</p>
                    {item.variant_name && <p className="text-xs text-amber-400">{item.variant_name} {item.variant_price ? `(${formatGs(item.variant_price)})` : ''}</p>}
                    {item.addons?.filter(a => a.addon_name).map(a => (
                      <p key={a.id} className="text-xs text-zinc-500">+ {a.addon_name} ({formatGs(a.extra_cost)})</p>
                    ))}
                  </div>
                  <p className="text-sm font-medium shrink-0">{formatGs(item.subtotal)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-zinc-800/50 p-4 space-y-1.5">
              <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span>{formatGs(order.subtotal)}</span></div>
              <div className="flex justify-between text-sm text-zinc-400"><span>Envio</span><span>{formatGs(order.delivery)}</span></div>
              {order.descuento > 0 && <div className="flex justify-between text-sm text-emerald-400"><span>Descuento</span><span>-{formatGs(order.descuento)}</span></div>}
              <div className="flex justify-between font-bold text-base pt-1 border-t border-zinc-800/30"><span>Total</span><span className="text-[#E84C1C]">{formatGs(order.total)}</span></div>
              <p className="text-xs text-zinc-500">Pago: {order.metodo_pago === 'efectivo' ? 'Efectivo' : 'Tarjeta POS'}</p>
            </div>
          </div>

          {/* Status history */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-3">Historial de estados</h3>
            <div className="space-y-2">
              {(order.status_history || []).map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  <div className="w-2 h-2 rounded-full bg-[#E84C1C] shrink-0" />
                  <span className="text-zinc-300 font-medium">{ORDER_STATUS[h.status]?.label || h.status}</span>
                  <span className="text-zinc-600">-</span>
                  <span className="text-zinc-500">{h.changed_by_name || 'Sistema'}</span>
                  <span className="text-zinc-600 ml-auto">{new Date(h.created_at).toLocaleString('es-PY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right - Actions */}
        <div className="lg:w-72 space-y-4">
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4 space-y-3">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Acciones</h3>
            {transitions.map(t => (
              t.needsReason ? (
                <div key={t.to}>
                  {!showReject ? (
                    <button onClick={() => setShowReject(true)} disabled={updating} className={`w-full text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 ${t.style}`} data-testid={`order-action-${t.to}`}>{t.label}</button>
                  ) : (
                    <div className="space-y-2">
                      <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Motivo del rechazo..." className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-sm resize-none h-20 outline-none focus:border-red-500" />
                      <button onClick={() => updateStatus(t.to)} disabled={updating} className="w-full bg-red-600 text-white text-sm font-medium py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">Confirmar rechazo</button>
                      <button onClick={() => setShowReject(false)} className="w-full text-xs text-zinc-500 hover:text-white">Cancelar</button>
                    </div>
                  )}
                </div>
              ) : (
                <button key={t.to} onClick={() => updateStatus(t.to)} disabled={updating} className={`w-full text-white text-sm font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50 ${t.style}`} data-testid={`order-action-${t.to}`}>{t.label}</button>
              )
            ))}
            {transitions.length === 0 && <p className="text-xs text-zinc-500">No hay acciones disponibles para este estado.</p>}
            <hr className="border-zinc-800" />
            <button onClick={handlePrint} className="w-full flex items-center justify-center gap-2 text-sm text-zinc-300 py-2 rounded-lg border border-zinc-700 hover:bg-zinc-800 transition-colors" data-testid="order-print-btn">
              <Printer size={16} /> Imprimir ticket
            </button>
          </div>

          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl p-4">
            <h3 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">Info</h3>
            <div className="space-y-1.5 text-xs">
              <div className="flex justify-between"><span className="text-zinc-500">Sucursal</span><span className="text-zinc-300">{order.branch_name}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Tipo</span><span className={`font-medium ${order.order_type === 'pickup' ? 'text-amber-400' : 'text-emerald-400'}`}>{order.order_type === 'pickup' ? 'Retiro' : 'Delivery'}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Metodo pago</span><span className="text-zinc-300">{order.metodo_pago}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden printable ticket */}
      <div className="hidden">
        <div ref={ticketRef}>
          <h2>SUSHI EXPRESS</h2>
          <p>Sucursal: {order.branch_name}</p>
          <p>{order.order_type === 'pickup' ? 'RETIRO EN LOCAL' : 'DELIVERY'}</p>
          {order.order_type === 'pickup' && order.pickup_ready_at && (
            <p>Retiro estimado: {new Date(order.pickup_ready_at).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</p>
          )}
          <hr />
          <p className="bold">Pedido #{order.id}</p>
          <p>{new Date(order.created_at).toLocaleString('es-PY')}</p>
          <hr />
          <p>Cliente: {order.customer_name}</p>
          {order.address_line && <p>Dir: {order.address_line}</p>}
          {order.delivery_address_text && <p>Dir detallada: {order.delivery_address_text}</p>}
          {order.nota_general && <div className="note"><b>NOTA:</b> {order.nota_general}</div>}
          <hr />
          {(order.items || []).map(item => (
            <div key={item.id}>
              <div className="row"><span>{item.quantity}x {item.product_name}</span><span>{formatGs(item.subtotal)}</span></div>
              {item.addons?.filter(a => a.addon_name).map(a => <p key={a.id} style={{ paddingLeft: '8px', fontSize: '10px' }}>+ {a.addon_name}</p>)}
            </div>
          ))}
          <hr />
          <div className="row"><span>Subtotal:</span><span>{formatGs(order.subtotal)}</span></div>
          <div className="row"><span>Envio:</span><span>{formatGs(order.delivery)}</span></div>
          <div className="row bold"><span>TOTAL:</span><span>{formatGs(order.total)}</span></div>
          <p>Pago: {order.metodo_pago === 'efectivo' ? 'Efectivo' : 'Tarjeta POS'}</p>
          <hr />
          <p style={{ textAlign: 'center', fontSize: '10px' }}>Gracias por tu pedido!</p>
        </div>
      </div>

      {/* Manual copy modal */}
      {manualCopyUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setManualCopyUrl(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 w-full max-w-md mx-4 space-y-3" data-testid="manual-copy-modal" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-medium text-zinc-200">Copiá manualmente</p>
            <input ref={manualInputRef} readOnly value={manualCopyUrl} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-300 outline-none select-all" data-testid="manual-copy-input" />
            <div className="flex gap-2">
              <button onClick={() => { manualInputRef.current?.select(); }} className="flex-1 text-xs font-medium py-2 rounded-lg bg-[#E84C1C] text-white hover:bg-[#d43f12] transition-colors" data-testid="manual-copy-select-btn">Seleccionar todo</button>
              <button onClick={() => setManualCopyUrl(null)} className="flex-1 text-xs font-medium py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:bg-zinc-800 transition-colors">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
